"use client";

import React, { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import Logo from "@/components/icons/Logo";
import { usePathname, useSearchParams } from "next/navigation";
import useNavRetract from "@/hooks/useNavRetract";
import If from "@/components/common/If";
import { Details } from "../Details";
import { Transition } from "@/components/common/Transition";
import { useNav } from "@/providers/Nav";
import { NavGuts } from "../NavGuts";
import { Button } from "@/components/actions/Button";
import { SearchIcon, X } from "lucide-react";
import SearchBar from "@/components/search/SearchBar";
import SearchResults from "@/components/search/SearchResults";
import { SearchResult } from "@/lib/types";
import { isExternalNavHref, toDetailItems } from "./nav-content";

/**
 *
 * Nav
 *
 */
const Nav = ({
  solidNavHandles = [],
  hiddenNavHandles = [],
  ...props
}: {
  /** Page handles that always get the solid nav (Feature Listing pages). */
  solidNavHandles?: string[];
  /** Handles where the main nav is hidden entirely (Link Pages). */
  hiddenNavHandles?: string[];
  [key: string]: any;
}) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { setNavRetracted, showAnnouncement, topNavItems } = useNav();
  const { isHeaderVisible, isLowerThanNav } = useNavRetract({ pathname });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Check localStorage for alert dismissal
  const getAlertState = () => {
    if (typeof window === "undefined") return pathname === "/";
    const dismissed = localStorage.getItem("alert-dismissed");
    return dismissed !== "true" && pathname === "/";
  };

  const [alertOpen, setAlertOpen] = useState(getAlertState());
  const [openTab, setOpenTab] = useState(-1);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);

  const closeOnEscape = useCallback((e: KeyboardEvent) => {
    if (e.key !== "Escape") return;

    setSearchOpen(false);
    setMobileMenuOpen(false);
    setOpenTab(-1);
  }, []);

  useEffect(() => {
    setOpenTab(-1);
    setMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    setNavRetracted(!isHeaderVisible);
  }, [isHeaderVisible, setNavRetracted]);

  useEffect(() => {
    setOpenTab(-1);
    setMobileMenuOpen(false);
  }, [searchParams]);

  useEffect(() => {
    if (!searchOpen) {
      setSearchResults([]);
    } else {
      setAlertOpen(false);
    }
  }, [searchOpen]);

  useEffect(() => {
    if (openTab >= topNavItems.length) {
      setOpenTab(-1);
    }
  }, [openTab, topNavItems]);

  useEffect(() => {
    const shouldListen = searchOpen || mobileMenuOpen || openTab > -1;
    if (!shouldListen) return;

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [closeOnEscape, mobileMenuOpen, openTab, searchOpen]);

  const isSolidNavPage = solidNavHandles.some(
    (handle) => pathname === `/${handle}` || pathname?.startsWith(`/${handle}/`),
  );

  const getBackground = () => {
    if (
      isSolidNavPage ||
      pathname?.includes("/technical-resources") ||
      pathname?.includes("/learning-center") ||
      pathname?.includes("/news") ||
      pathname === "/" ||
      pathname?.includes("/customer-service-contacts") ||
      pathname?.includes("/find-a-rep") ||
      pathname?.includes("/blog") ||
      pathname?.includes("/wood-veneer-part-1") ||
      pathname?.includes("/wood-veneer-part-2") ||
      pathname?.includes("/needhelp") ||
      pathname?.includes("/whats-happening") ||
      pathname?.includes("/case-studies") ||
      pathname?.includes("/acousticsguide")
    ) {
      return "bg-nav";
    }

    if (isLowerThanNav || openTab > -1) {
      return "bg-black";
    }

    return "bg-[transparent] border-b border-b-[#9B9B9B]";
  };

  if (hiddenNavHandles.some((handle) => pathname === `/${handle}`)) {
    return null;
  }

  const activeNavItem = topNavItems[openTab];
  const activeNavItemHasChildren = Boolean(activeNavItem?.items.length);

  return (
    <>
      <header
        className={`main-nav fixed top-0 left-0 right-0 z-[15002] transition-all duration-300 w-[100dvw] ease max-w-full ${
          isLowerThanNav && !isHeaderVisible ? "retract" : ""
        } ${openTab > -1 ? "bg-black border-b border-b-[#9B9B9B]" : getBackground()}`}
        {...props}
      >
        <If condition={pathname === "/" && showAnnouncement && openTab === -1}>
          <If condition={alertOpen}>
            <aside className="bg-black text-white px-gutter py-8 stack gap-6 items-start justify-center text-left font-bold relative z-[10000]">
              <div className="stack gap-4">
                <h2 className="uppercase text-2xl">
                  Masonite Architectural is Now Forte Opening Solutions!
                </h2>
                <p className="max-w-3xl text-base font-normal">
                  This rebranding marks an exciting new chapter for us, and we
                  can’t wait to share what it means. Forte isn’t just a new
                  name, it’s a reflection of our commitment to delivering
                  premium value and showcasing our deep industry expertise.
                </p>
              </div>
              <div className="flex flex-col md:flex-row gap-4 md:items-center">
                <Button size="small" isLight href="/news">
                  Read Press Release
                </Button>
                <Button size="small" isLight href="/about#sizzle">
                  Learn More
                </Button>
              </div>

              <button
                onClick={() => {
                  setAlertOpen(false);
                  localStorage.setItem("alert-dismissed", "true");
                }}
                className="absolute top-8 right-8 z-10"
              >
                <X color="white" />
                <span className="sr-only">Close</span>
              </button>
            </aside>
          </If>
        </If>
        <div className="flex justify-between items-center px-4 md:px-gutter py-4">
          <Link href="/">
            <Logo className="w-[125px] md:w-[239px]" />
          </Link>
          <button
            onClick={() => {
              setMobileMenuOpen(!mobileMenuOpen);
            }}
            className="flex relative z-[10000] md:hidden pulse bg-gold w-[35px] h-[35px] items-center justify-center rounded-full aspect-[1/1]"
          >
            <span className="sr-only">Menu</span>
          </button>
          <div className="hidden lg:flex flex-col gap-4 items-end">
            <Link
              href="https://masonitearchprod.powerappsportals.com/SignIn"
              className="opacity-50 hover:opacity-100 transition-all duration-300"
            >
              Customer Portal
            </Link>
            <div className="hidden lg:flex items-center gap-gutter text-nav-text nav-links">
              {topNavItems.map((item, index) => {
                if (item.items.length > 0) {
                  return (
                    <button
                      key={item.href || item.title}
                      onClick={() => setOpenTab(openTab === index ? -1 : index)}
                      className={`${openTab === index ? "active" : ""}`}
                    >
                      {item.title}
                    </button>
                  );
                }

                if (item.href) {
                  const isExternal = isExternalNavHref(item.href);

                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      target={isExternal ? "_blank" : undefined}
                      rel={isExternal ? "noopener noreferrer" : undefined}
                      className={`${openTab === index ? "active" : ""}`}
                    >
                      {item.title}
                    </Link>
                  );
                }

                return (
                  <span key={item.title} className="opacity-50">
                    {item.title}
                  </span>
                );
              })}
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="cursor-pointer"
              >
                <SearchIcon />
              </button>
            </div>
          </div>
        </div>
      </header>

      <If condition={mobileMenuOpen}>
        <div
          className="fixed top-0 left-0 w-full h-full bg-maroon overflow-y-auto z-[15001] px-4 pt-35 pb-25"
          data-lenis-prevent
        >
          <Link href="https://portal.forteopenings.com/">Customer Portal</Link>
          <NavGuts />
        </div>
      </If>

      <If condition={searchOpen}>
        <div
          className="fixed top-0 left-0 w-full h-full bg-teal overflow-y-auto z-[15001] px-4 pt-35 pb-25 fade-in"
          data-lenis-prevent
        >
          <svg
            className="w-full absolute left-0 top-0 z-0 opacity-50 pointer-events-none"
            viewBox="0 0 1440 1440"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M478.5 0.500124L958.495 0.618991L478.5 359.003V0.500124Z"
              stroke="#185B56"
            ></path>
            <path
              d="M959.5 0.500124L1437.5 0.61899L959.5 359V0.500124Z"
              stroke="#197875"
            ></path>
            <path
              d="M0.5 358.5L479.498 358.619L0.5 717.001V358.5Z"
              stroke="#197875"
            ></path>
            <path
              d="M0.5 0.500124L479.498 0.618991L0.5 359.001V0.500124Z"
              stroke="#197875"
            ></path>
            <path
              d="M479.5 358.5L958.498 358.619L479.5 717.001V358.5Z"
              stroke="#197875"
            ></path>
            <path
              d="M959.5 358.5L1438.5 358.619L959.5 717.001V358.5Z"
              stroke="#197875"
            ></path>
            <path
              d="M960.5 721.5L1439.5 721.619L960.5 1080V721.5Z"
              stroke="#197875"
            ></path>
            <path
              d="M481.5 1081.5L960.498 1081.62L481.5 1440V1081.5Z"
              stroke="#197875"
            ></path>
          </svg>
          <button
            onClick={() => setSearchOpen(false)}
            className="absolute top-[90px] right-4 text-gold z-[15002]"
          >
            <X className="w-12 h-12" strokeWidth={1} />
          </button>
          <div className="stack gap-2 text-white  z-[15002] relative">
            <SearchBar onResultsChange={setSearchResults} />
            <SearchResults results={searchResults} />
          </div>
        </div>
      </If>

      {openTab > -1 && activeNavItem && activeNavItem.items.length > 0 && (
        <>
          <Transition
            isVisible={openTab > -1}
            enterAnimation="fadeIn"
            className="fixed top-0 left-0 w-full h-full blur z-[15001]"
            duration={300}
            onClick={(ev) => {
              ev.stopPropagation();
              setOpenTab(-1);
            }}
          >
            <></>
          </Transition>
          <Transition
            isVisible={openTab > -1}
            enterAnimation="slideIn"
            as="nav"
            className="nav fixed top-0 right-0 z-[15001] transition-all duration-300 w-[660px] ease max-w-full max-h-[100dvh] overflow-y-auto bg-maroon px-gutter pt-35 pb-25 flex flex-col"
            data-lenis-prevent
            key={activeNavItem.title || openTab}
          >
            <button
              onClick={() => setOpenTab(-1)}
              className="absolute top-28 right-gutter z-[15002] transition-all duration-300"
            >
              <svg
                width="33"
                height="32"
                viewBox="0 0 33 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <line
                  x1="0.943322"
                  y1="0.943322"
                  x2="32.0564"
                  y2="31.3489"
                  stroke="#B7974A"
                />
                <line
                  x1="0.943688"
                  y1="31.3496"
                  x2="31.3493"
                  y2="0.943981"
                  stroke="#B7974A"
                />
              </svg>
            </button>

            <Details items={toDetailItems(activeNavItem.items)} />
          </Transition>
        </>
      )}
    </>
  );
};

export default Nav;
